'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Rating, Tooltip } from '@mantine/core';

import { Icon } from '@/elements/icon/icon.component';
import { MediaImage } from '@/elements/media-image/media-image.component';
import { ModalVideoWrapper as ModalVideo } from '@/elements/modal-video/modal-video.component';
import {
  CardWrapper,
  ImageWrap,
  PlayWrap,
  Content,
  Title,
  Footer,
  Ribbon,
  TopImageSection,
  BottomImageSection,
  RegistrationCounter,
  Venues,
  Venue,
  Feature,
  Grade,
} from './education-card.elements';

import { findNextUpcomingDate } from '@/utils/functions.utils';
import { convertMinutes } from '@/utils/functions.utils';
import { hasActiveDiscount } from '@/helpers/education-filters.helpers';

import {
  IconSchool,
  IconHexagons,
  IconClockHour3,
  IconCalendarPin,
  IconVideo,
  IconScreenShare,
  IconPlayerPlayFilled,
} from '@tabler/icons-react';

const EducationCard = ({
  data: {
    slug,
    image,
    modules: { modules },
    plans: { educationPlans },
    type,
    title,
    averageGrade,
    gradeCount,
    venues,
    settings: { trailerId, ribbon, ribbonColor, ribbonExpiresAt },
  },
  type: cardType,
}) => {
  const [showVideo, setShowVideo] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleShowTrailer = (e) => {
    e.preventDefault();
    setShowVideo(true);
  };

  const nextEvent = findNextUpcomingDate(modules, 'lectureStartDateTime');
  const isFree = educationPlans.length === 1 && educationPlans[0].priceTotal === 0;
  const hasRibbon =
    ribbon?.length > 0 && (ribbonExpiresAt ? new Date(ribbonExpiresAt) > new Date() : true);
  const hasDiscount = hasActiveDiscount({ plans: { educationPlans } });

  // Get the highest discount percentage from active discounts
  const getDiscountValue = () => {
    if (!hasDiscount) return 0;

    const now = new Date();
    const activeDiscounts = educationPlans
      .filter((planItem) => {
        const hasActiveFrom = planItem.activeFrom && planItem.activeFrom !== '';
        const hasActiveTo = planItem.activeTo && planItem.activeTo !== '';

        // If neither is set, include the item
        if (!hasActiveFrom && !hasActiveTo) return true;

        // Check only the constraints that are set
        const fromCheck = !hasActiveFrom || new Date(planItem.activeFrom) < now;
        const toCheck = !hasActiveTo || new Date(planItem.activeTo) > now;

        return fromCheck && toCheck;
      })
      .filter((planItem) => {
        const discount = planItem.discount || 0;
        const discountExpiresAt = planItem.discountExpiresAt;

        if (discount > 0) {
          if (!discountExpiresAt) return true;
          return new Date(discountExpiresAt) > now;
        }
        return false;
      })
      .map((planItem) => planItem.discount);

    return Math.max(...activeDiscounts, 0);
  };

  const discountValue = getDiscountValue();

  const limitedPlan = educationPlans.find((plan) => plan.limitRegistrations);
  const limitRegistrations = limitedPlan?.limitRegistrations;
  const showRegistrationCount = limitedPlan?.showRegistrationCount;
  const maxRegistrations = limitedPlan?.maxRegistrations;
  const registrationCount = limitedPlan?.registrationCount;
  const recordingOnly =
    educationPlans.length === 1 && educationPlans[0].plan.features.hasRecording === true;

  const venueBadges = {
    online: (
      <Tooltip
        label="Online sudjelovanje"
        events={{ hover: true, focus: true }}
        withArrow
        transitionProps={{ duration: 200 }}
      >
        <Venue $color="lagoon">
          <Icon color="light">
            <IconScreenShare size={16} />
          </Icon>
        </Venue>
      </Tooltip>
    ),
    live: (
      <Tooltip
        label="Uživo sudjelovanje"
        events={{ hover: true, focus: true }}
        withArrow
        transitionProps={{ duration: 200 }}
      >
        <Venue $color="aqua">
          <Icon color="light">
            <IconSchool size={16} />
          </Icon>
        </Venue>
      </Tooltip>
    ),
    recording: (
      <Tooltip
        label="Snimka"
        events={{ hover: true, focus: true }}
        withArrow
        transitionProps={{ duration: 200 }}
      >
        <Venue $color="simba">
          <Icon color="light">
            <IconVideo size={16} />
          </Icon>
        </Venue>
      </Tooltip>
    ),
  };

  return (
    <>
      <Link
        href={`/${slug}${cardType === 'gift' ? '?vrsta=gift-education' : ''}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <CardWrapper>
          <ImageWrap $hasTrailer={!!trailerId}>
            <MediaImage
              variant="education"
              src={image?.sizes?.educationCard?.url}
              alt={image?.alt || title}
              width={300}
              height={215}
              style={{ objectFit: 'contain' }}
            />
            {trailerId ? (
              <PlayWrap onClick={handleShowTrailer}>
                <IconPlayerPlayFilled size={36} />
              </PlayWrap>
            ) : null}
            <TopImageSection>
              {hasRibbon ? (
                <Ribbon $color={ribbonColor}>
                  <span>{ribbon}</span>
                </Ribbon>
              ) : null}
              {recordingOnly && (
                <Ribbon $color="simba">
                  <span>SNIMKA</span>
                </Ribbon>
              )}
              {hasDiscount && !isFree && discountValue > 0 ? (
                <Ribbon $color="red">
                  <span>-{discountValue}%</span>
                </Ribbon>
              ) : null}
              {limitRegistrations && showRegistrationCount ? (
                <RegistrationCounter>
                  {maxRegistrations - registrationCount > 0
                    ? `Još samo ${maxRegistrations - registrationCount} mjest${maxRegistrations - registrationCount === 1 ? 'o' : 'a'}!`
                    : 'POPUNJENO'}
                </RegistrationCounter>
              ) : null}
            </TopImageSection>
            <BottomImageSection>
              <Venues>
                {venues.map((venue) => (
                  <span key={venue}>{venueBadges[venue]}</span>
                ))}
              </Venues>
            </BottomImageSection>
          </ImageWrap>
          <Content>
            <Title>{title}</Title>
            <Feature>
              <Icon color="blue">
                {type === 'webinar' ? <IconSchool size={20} /> : <IconHexagons size={20} />}
              </Icon>
              {type === 'webinar'
                ? (modules[0].lecturers || [])
                    .map((entry) => {
                      const lecturer = entry?.lecturer;
                      return `${lecturer?.firstName || ''} ${lecturer?.lastName || ''}`.trim();
                    })
                    .filter(Boolean)
                    .join(', ')
                : modules.length === 1
                  ? `1 modul`
                  : `${modules.length} modula`}
            </Feature>
            {(averageGrade < 5 && gradeCount >= 3) || averageGrade > 3 ? (
              <Grade>
                <span>{averageGrade.toFixed(1)}</span>
                {mounted && (
                  <Rating
                    id={`education-rating-${slug}`}
                    size="xs"
                    fractions={10}
                    value={averageGrade}
                    readOnly
                  />
                )}
                <span>({gradeCount})</span>
              </Grade>
            ) : null}
            <Footer>
              <Feature>
                <Icon color="blue">
                  <IconClockHour3 size={20} />
                </Icon>
                <span>
                  {convertMinutes(modules.reduce((sum, module) => sum + module.lectureDuration, 0))}
                </span>
              </Feature>
              <Feature>
                {nextEvent ? (
                  <>
                    <Icon color="blue">
                      <IconCalendarPin size={20} />
                    </Icon>
                    <span>
                      {new Intl.DateTimeFormat('hr', {
                        day: 'numeric',
                        month: 'narrow',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                        timeZone: 'Europe/Zagreb',
                      }).format(new Date(nextEvent))}
                    </span>
                  </>
                ) : null}
              </Feature>
            </Footer>
          </Content>
        </CardWrapper>
      </Link>
      {showVideo && (
        <ModalVideo
          channel="vimeo"
          autoplay
          isOpen={showVideo}
          videoId={trailerId}
          onClose={() => setShowVideo(false)}
        />
      )}
    </>
  );
};

export { EducationCard };
